import React, { useState, useEffect, useContext } from 'react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Shield, Key, Link, Copy, Check, ExternalLink, AlertTriangle, 
  Loader2, Settings, Download, Upload, RefreshCw, Info
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

export const SSOConfigPage = () => {
  const token = localStorage.getItem('token');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    provider: 'saml',
    idp_entity_id: '',
    idp_sso_url: '',
    idp_certificate: '',
    enabled: false
  });
  const [copied, setCopied] = useState(null);
  const [metadataUrl, setMetadataUrl] = useState('');

  useEffect(() => {
    loadConfig();
    // Set metadata URL
    const backendUrl = API.replace('/api', '');
    setMetadataUrl(`${backendUrl}/api/sso/saml/metadata`);
  }, [API]);

  const loadConfig = async () => {
    try {
      const response = await axios.get(`${API}/sso/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setConfig({
          provider: response.data.provider || 'saml',
          idp_entity_id: response.data.idp_entity_id || '',
          idp_sso_url: response.data.idp_sso_url || '',
          idp_certificate: response.data.idp_certificate || '',
          enabled: response.data.enabled || false
        });
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Error loading SSO config:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!config.idp_entity_id || !config.idp_sso_url) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/sso/config`, config, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('SSO configuration saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save SSO configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to disable SSO? Users will need to login with email/password.')) {
      return;
    }

    setSaving(true);
    try {
      await axios.delete(`${API}/sso/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig({
        provider: 'saml',
        idp_entity_id: '',
        idp_sso_url: '',
        idp_certificate: '',
        enabled: false
      });
      toast.success('SSO configuration removed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove SSO configuration');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(null), 2000);
    toast.success('Copied to clipboard');
  };

  const downloadMetadata = async () => {
    try {
      const response = await axios.get(`${API}/sso/saml/metadata`);
      const blob = new Blob([response.data], { type: 'application/xml' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'mydocorbit-sp-metadata.xml';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Metadata downloaded');
    } catch (error) {
      toast.error('Failed to download metadata');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-indigo-400 animate-spin" />
      </div>
    );
  }

  const backendUrl = API.replace('/api', '');
  const acsUrl = `${backendUrl}/api/sso/saml/acs`;
  const entityId = `${backendUrl}/api/sso/saml`;

  return (
    <div className="p-8 bg-slate-950 min-h-full">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Shield className="w-8 h-8 text-indigo-400" />
            Single Sign-On (SSO)
          </h1>
          <p className="text-slate-400 mt-2">
            Configure SAML-based Single Sign-On for your organization
          </p>
        </div>

        {/* Plan Check */}
        {user?.subscription_plan && !['business', 'enterprise'].includes(user.subscription_plan) && (
          <Card className="bg-amber-500/10 border-amber-500/30 mb-6">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
                <div>
                  <p className="text-amber-200 font-medium">SSO requires Business or Enterprise plan</p>
                  <p className="text-amber-300/70 text-sm">Upgrade your plan to enable SSO for your organization</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Service Provider Info */}
        <Card className="bg-slate-900/50 border-slate-800 mb-6">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Key className="w-5 h-5 text-cyan-400" />
              Service Provider (SP) Details
            </CardTitle>
            <CardDescription className="text-slate-400">
              Use these values when configuring your Identity Provider (IdP)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4">
              <div>
                <Label className="text-slate-300 text-sm">Entity ID / Issuer</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    value={entityId}
                    readOnly
                    className="bg-slate-800 border-slate-700 text-slate-300 font-mono text-sm"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(entityId, 'entityId')}
                    className="text-slate-400 hover:text-white"
                  >
                    {copied === 'entityId' ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              <div>
                <Label className="text-slate-300 text-sm">ACS URL (Assertion Consumer Service)</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    value={acsUrl}
                    readOnly
                    className="bg-slate-800 border-slate-700 text-slate-300 font-mono text-sm"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(acsUrl, 'acsUrl')}
                    className="text-slate-400 hover:text-white"
                  >
                    {copied === 'acsUrl' ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              <div>
                <Label className="text-slate-300 text-sm">Metadata URL</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    value={metadataUrl}
                    readOnly
                    className="bg-slate-800 border-slate-700 text-slate-300 font-mono text-sm"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(metadataUrl, 'metadataUrl')}
                    className="text-slate-400 hover:text-white"
                  >
                    {copied === 'metadataUrl' ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={downloadMetadata}
                    className="text-slate-400 hover:text-white"
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex items-start gap-2 p-3 bg-slate-800/50 rounded-lg mt-4">
              <Info className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
              <p className="text-slate-400 text-sm">
                Configure your Identity Provider (Azure AD, Okta, Google Workspace, etc.) with the above values, 
                then enter your IdP details below.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* IdP Configuration */}
        <Card className="bg-slate-900/50 border-slate-800 mb-6">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Settings className="w-5 h-5 text-indigo-400" />
              Identity Provider (IdP) Configuration
            </CardTitle>
            <CardDescription className="text-slate-400">
              Enter the details from your Identity Provider
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-slate-300 text-sm">Provider Type</Label>
              <Select
                value={config.provider}
                onValueChange={(value) => setConfig({ ...config, provider: value })}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                  <SelectValue placeholder="Select provider" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="saml" className="text-white">SAML 2.0 (Generic)</SelectItem>
                  <SelectItem value="azure_ad" className="text-white">Azure Active Directory</SelectItem>
                  <SelectItem value="okta" className="text-white">Okta</SelectItem>
                  <SelectItem value="google" className="text-white">Google Workspace</SelectItem>
                  <SelectItem value="onelogin" className="text-white">OneLogin</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-slate-300 text-sm">IdP Entity ID / Issuer <span className="text-red-400">*</span></Label>
              <Input
                value={config.idp_entity_id}
                onChange={(e) => setConfig({ ...config, idp_entity_id: e.target.value })}
                placeholder="https://your-idp.com/entity-id"
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>

            <div>
              <Label className="text-slate-300 text-sm">IdP SSO URL <span className="text-red-400">*</span></Label>
              <Input
                value={config.idp_sso_url}
                onChange={(e) => setConfig({ ...config, idp_sso_url: e.target.value })}
                placeholder="https://your-idp.com/sso/saml"
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>

            <div>
              <Label className="text-slate-300 text-sm">IdP Certificate (X.509, PEM format)</Label>
              <textarea
                value={config.idp_certificate}
                onChange={(e) => setConfig({ ...config, idp_certificate: e.target.value })}
                placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
                rows={4}
                className="w-full bg-slate-800 border border-slate-700 text-white mt-1 rounded-md p-3 font-mono text-sm resize-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              <p className="text-slate-500 text-xs mt-1">Optional: Used to verify SAML responses</p>
            </div>

            <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
              <div>
                <p className="text-white font-medium">Enable SSO</p>
                <p className="text-slate-400 text-sm">Allow users to login via SSO</p>
              </div>
              <Switch
                checked={config.enabled}
                onCheckedChange={(checked) => setConfig({ ...config, enabled: checked })}
              />
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex items-center justify-between">
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={saving}
            className="bg-red-600 hover:bg-red-700"
          >
            Remove SSO Configuration
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                Save Configuration
              </>
            )}
          </Button>
        </div>

        {/* Help Section */}
        <Card className="bg-slate-900/50 border-slate-800 mt-8">
          <CardHeader>
            <CardTitle className="text-white text-lg">Setup Guides</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { name: 'Azure AD', url: 'https://docs.microsoft.com/azure/active-directory/saas-apps/tutorial-list' },
                { name: 'Okta', url: 'https://help.okta.com/en/prod/Content/Topics/Apps/Apps_App_Integration_Wizard_SAML.htm' },
                { name: 'Google Workspace', url: 'https://support.google.com/a/answer/6087519' },
                { name: 'OneLogin', url: 'https://onelogin.service-now.com/support' }
              ].map((guide) => (
                <a
                  key={guide.name}
                  href={guide.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 p-3 bg-slate-800/50 rounded-lg hover:bg-slate-800 transition-colors group"
                >
                  <Link className="w-4 h-4 text-slate-400 group-hover:text-indigo-400" />
                  <span className="text-slate-300 group-hover:text-white">{guide.name} Setup Guide</span>
                  <ExternalLink className="w-3 h-3 text-slate-500 ml-auto" />
                </a>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SSOConfigPage;
